"use client";
import Image from "next/image";
import { useState, useRef, useEffect, useMemo } from "react";
import { FiCalendar } from "react-icons/fi";
import styles from "@/assets/css/applicationTracker.module.css";
import apiClient from "@/helpers/api.helper";
import {
  EMAIL_VERIFICATION,
  EMAIL_VERIFICATION_VERIFY,
  STUDENT_PROFILE_WITH_PARENTS,
  STUDENT_GETREGISTRATION_EMAIL,
  COMMON_UTILITIES,
  DOCUMENT_UPLOAD,
  STUDENT_UPLOAD_PROFILE_PICTURE,
} from "@/constants/urls";

import TrackApplicationCard from "../components/publicWebsite/trackApplication/TrackApplicationCard";
import StudentForm from "../components/publicWebsite/studentForm/StudentForm";
import type { FormSection } from "../components/publicWebsite/studentForm/type";

import Link from "next/link";
import Button from "@/components/publicWebsite/ui/Buttons/Buttons";
import {
  StudentRegistration,
  TrackerCardStudent,
} from "@/models/api/studentRegistration";
import SubmitSuccessModal from "@/components/publicWebsite/ui/SubmitSuccessModal/SubmitSuccessModal";
import type { Utility } from "@/models/api/utility";
import ProgressBar from "../components/publicWebsite/ui/Progressbar/ProgressBar";
import { FileText } from "lucide-react";
import ProgressHistory from "@/components/publicWebsite/ui/ProgressHistory/ProgressHistory";
import { useQuery } from "@tanstack/react-query";
import type { UploadResponse } from "@/models/api/studentRegistration";
import ToggleSwitch from "../components/publicWebsite/ui/ToggleSwitch/ToggleSwitch";
import { buildAdmissionFormConfig } from "@/components/publicWebsite/studentForm/admissionFormConfig";
import {
  CAMPUS_VISIT_LOCATION,
  CAMPUS_VISIT_LOCATION_GOOGLE_URL,
} from "@/constants/env";

/* ---------------- Status helpers ---------------- */
type StatusCode =
  | "SUBMITTED"
  | "REVIEWED"
  | "VISITSCHEDULED"
  | "APPROVED"
  | "ENROLLED";

function getCurrentStatusCode(
  reg?: StudentRegistration
): StatusCode | undefined {
  const curr = reg?.registrationStatus?.code as StatusCode | undefined;
  if (curr) return curr;
  const latest = [...(reg?.studentStatuses ?? [])]
    .filter((s) => s.status?.code)
    .sort(
      (a, b) =>
        new Date(b.statusDate ?? 0).getTime() -
        new Date(a.statusDate ?? 0).getTime()
    )[0];
  return latest?.status?.code as StatusCode | undefined;
}
function mapStatusCodeToUiString(
  code?: StatusCode
):
  | "submitted"
  | "reviewed"
  | "visitScheduled"
  | "approved"
  | "enrolled"
  | "pending" {
  switch (code) {
    case "SUBMITTED":
      return "submitted";
    case "REVIEWED":
      return "reviewed";
    case "VISITSCHEDULED":
      return "visitScheduled";
    case "APPROVED":
      return "approved";
    case "ENROLLED":
      return "enrolled";
    default:
      return "pending";
  }
}
function formatIST(dateISO?: string, timeHHMMSS?: string) {
  if (!dateISO) return null;
  const dt = new Date(
    timeHHMMSS ? `${dateISO.substring(0, 10)}T${timeHHMMSS}` : dateISO
  );
  if (isNaN(dt.getTime())) return null;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: timeHHMMSS ? "2-digit" : undefined,
    minute: timeHHMMSS ? "2-digit" : undefined,
  }).format(dt);
}
function formatISTDateOnly(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}
function getStatusDateIso(reg?: StudentRegistration, code?: StatusCode) {
  if (!reg?.studentStatuses?.length || !code) return null;
  const hit = reg.studentStatuses.find((s) => s.status?.code === code);
  return hit?.statusDate ?? null;
}

const ApplicationTrackerForm = () => {
  const [step, setStep] = useState<"email" | "otp" | "tracker">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] =
    useState<TrackerCardStudent | null>(null);
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // toggles for the single Services section
  const [transportEnabled, setTransportEnabled] = useState(true);
  const [canteenEnabled, setCanteenEnabled] = useState(true);
  const [hostelEnabled, setHostelEnabled] = useState(true);

  // raw registrations map for prefill
  const [registrationsById, setRegistrationsById] = useState<
    Record<string, StudentRegistration>
  >({});
  const [students, setStudents] = useState<TrackerCardStudent[]>([]);



  const mapRegistrationToCard = (
    r: StudentRegistration
  ): TrackerCardStudent => {
    const latestCode = getCurrentStatusCode(r);

    // Extract yearly fees from the API response
    // It's in r.course.classes[0].yearlyFees
    const yearlyFees = r.course?.classes?.[0]?.yearlyFees || null;

    return {
      id: r.id,
      profileId: r.studentProfile?.id ?? null,
      name: [r.firstName, r.lastName].filter(Boolean).join(" ").trim() || "—",
      parent: (r.fatherName || r.motherName || r.guardianName || "—") as string,
      entryYear: r.proposedEntryYear ? String(r.proposedEntryYear) : "—",
      applyingGrade:
        r.class?.name ||
        r.currentGradeLevel?.trim() ||
        (r.applyingGradeLevel ?? "—"),
      dateOfBirth: r.dateOfBirth ? r.dateOfBirth.slice(0, 10) : undefined,
      status: mapStatusCodeToUiString(latestCode),
      courseDisplayName: r.course?.displayName ?? r.course?.name ?? "",
      courseCode: r.course?.code ?? "",
      phone: r.phone ?? null,

      // ADD THIS LINE
      yearlyFees: yearlyFees,
    };
  };

  /** ---------- Utilities -> StudentRelation dropdown ---------- */
  const relationUtilsQuery = useQuery<Utility[], Error>({
    queryKey: ["utilities", "StudentRelation"],
    queryFn: async () => {
      const data = await apiClient.get<Utility[]>(COMMON_UTILITIES);
      return Array.isArray(data)
        ? data.filter((u) => u.isActive && u.type === "StudentRelation")
        : [];
    },
    staleTime: 5 * 60_000,
  });

  // --------- Get Confg section (three feature groups) ----------

  const admissionFormConfig = useMemo(
    () =>
      buildAdmissionFormConfig({
        transport: transportEnabled,
        canteen: canteenEnabled,
        hostel: hostelEnabled,
      }),
    [transportEnabled, canteenEnabled, hostelEnabled]
  );

  /** ---------- Fetch registrations by email ---------- */
  const fetchRegistrationsByEmail = async (em: string) => {
    try {
      const payload = await apiClient.get<
        StudentRegistration | StudentRegistration[]
      >(STUDENT_GETREGISTRATION_EMAIL(em));
      const items: StudentRegistration[] = Array.isArray(payload)
        ? payload
        : payload
          ? [payload]
          : [];
      setStudents(items.map(mapRegistrationToCard));
      const map: Record<string, StudentRegistration> = {};
      for (const r of items) map[r.id] = r;
      setRegistrationsById(map);
    } catch (e: any) {
      setStudents([]);
      setRegistrationsById({});
    }
  };

  const toPrefill = (r: StudentRegistration) => ({
    firstName: r.firstName ?? "",
    lastName: r.lastName ?? "",
    dateOfBirth: r.dateOfBirth ? r.dateOfBirth.slice(0, 10) : "",
    gender: r.gender
      ? (r.gender as string).charAt(0).toUpperCase() +
      (r.gender as string).slice(1).toLowerCase()
      : "",
    mobileNo: r.phone ?? "",
    email: r.email ?? "",
    courseFees: r.course?.classes?.[0]?.yearlyFees || 0,
  });
  function formatISTTimeOnly(
    dateISO?: string | null,
    timeHHMMSS?: string | null
  ) {
    if (!dateISO || !timeHHMMSS) return null;
    // tolerate "HH:MM" or "HH:MM:SS"
    const t = /^\d{2}:\d{2}(:\d{2})?$/.test(timeHHMMSS) ? timeHHMMSS : null;
    if (!t) return null;

    const isoDate = dateISO.substring(0, 10); // YYYY-MM-DD
    const dt = new Date(`${isoDate}T${t}`);
    if (isNaN(dt.getTime())) return null;

    return new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
    }).format(dt);
  }

  const handleUploadDocuments = async (files: { tenth?: File; twelfth?: File }) => {
    if (!selectedStudent?.id) {
      alert("No student selected.");
      return;
    }

    const tasks: Promise<any>[] = [];
    if (files.tenth) {
      tasks.push(uploadOneDocument("Student", String(selectedStudent.id), files.tenth));
    }
    if (files.twelfth) {
      tasks.push(uploadOneDocument("Student", String(selectedStudent.id), files.twelfth));
    }

    if (!tasks.length) {
      alert("Please choose a document to upload.");
      return;
    }

    await Promise.all(tasks);
    // alert("Document(s) uploaded successfully.");
  };


  const selectedRegistration = selectedStudent
    ? registrationsById[selectedStudent.id]
    : undefined;
  const currentCode = getCurrentStatusCode(selectedRegistration);
  const barStatus = mapStatusCodeToUiString(currentCode);
  const latestVisit = [...(selectedRegistration?.visitSchedules ?? [])].pop();
  const visitStart = formatIST(
    latestVisit?.schoolVisitSlot?.slotDate,
    latestVisit?.schoolVisitSlot?.startTime
  );
  const visitDate = formatISTDateOnly(latestVisit?.schoolVisitSlot?.slotDate);
  const visitStartTime = formatISTTimeOnly(
    latestVisit?.schoolVisitSlot?.slotDate,
    latestVisit?.schoolVisitSlot?.startTime
  );
  const visitEndTime = formatISTTimeOnly(
    latestVisit?.schoolVisitSlot?.slotDate,
    latestVisit?.schoolVisitSlot?.endTime ?? null
  );
  const visitEnd = latestVisit?.schoolVisitSlot?.endTime
    ? formatIST(
      latestVisit.schoolVisitSlot.slotDate,
      latestVisit.schoolVisitSlot.endTime
    )
    : null;

  async function uploadOneDocument(
    entity: string,
    entityId: string,
    file: File
  ) {
    return apiClient.uploadBlob<UploadResponse>(DOCUMENT_UPLOAD, file, "File", {
      Entity: entity,
      EntityId: entityId,
      FileName: file.name,
    });
  }

  const getRelationGuidByName = (name: string) => {
    const row = (relationUtilsQuery.data ?? []).find((u) => {
      const label = (u as any).name ?? (u as any).code ?? (u as any).type ?? "";
      return String(label).trim().toLowerCase() === name.toLowerCase();
    });
    return (row as any)?.id ?? null;
  };
  function toISO(d?: string | null): string | null {
    if (!d) return null;
    // If already YYYY-MM-DD, assume local midnight and convert to ISO.
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const dt = new Date(`${d}T00:00:00Z`); // or omit Z if you want local time
      return isNaN(dt.getTime()) ? null : dt.toISOString();
    }
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt.toISOString();
  }
  function normalizeGender(g?: string) {
    const t = (g || "").trim().toLowerCase();
    if (t.startsWith("m")) return "Male";
    if (t.startsWith("f")) return "Female";
    if (t.startsWith("o")) return "Other";
    return "";
  }

  // const fatherRelationGuid = getRelationGuidByName("Father");
  // const motherRelationGuid = getRelationGuidByName("Mother");

  // const buildParent = (args: {
  //   firstName?: string;
  //   email?: string;
  //   phone?: string;
  //   occupation?: string;
  //   income?: any;
  //   relationId: string | null;
  //   nationalId?: string;
  //   workPlaceDetail?: string;
  // }) => ({
  //   firstName: args.firstName || "",
  //   lastName: "",
  //   relationId: args.relationId,
  //   nationalId: args.nationalId || "",
  //   occupation: args.occupation || "",
  //   email: args.email || "",
  //   phone: args.phone || "",
  //   income: args.income === "" || args.income == null ? 0 : Number(args.income),
  //   workPlaceDetail: args.workPlaceDetail || "",
  //   isActive: true,
  // });
  const buildParent = (args: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    occupation?: string;
    income?: any;
    relationship: "Father" | "Mother";
    gender?: string;                 // "Male" | "Female" | "Other"
    nationalId?: string;
    workPlaceDetail?: string;
  }) => ({
    firstName: args.firstName || "",
    lastName: args.lastName || "",
    relationship: args.relationship,           // <-- hard-coded label, not id
    gender: normalizeGender(args.gender),      // <-- pass parent's gender
    nationalId: args.nationalId || "",
    occupation: args.occupation || "",
    email: args.email || "",
    phone: args.phone || "",
    income: args.income === "" || args.income == null ? 0 : Number(args.income),
    workPlaceDetail: args.workPlaceDetail || "",
    isActive: true,
  });

  // const toProfilePayload = (form: Record<string, any>): any => {
  //   const father = buildParent({
  //     firstName: form.fathersName,
  //     email: form.fatherEmail,
  //     phone: form.fatherMobile,
  //     occupation: form.fatherOccupation,
  //     income: form.fatherIncome,
  //     relationId: fatherRelationGuid,
  //     nationalId: form.fatherNationalId,
  //     workPlaceDetail: form.workPlaceDetail,
  //   });

  //   const mother = buildParent({
  //     firstName: form.motherName,
  //     email: form.motherEmail,
  //     phone: form.motherMobile,
  //     occupation: form.motherOccupation,
  //     income: form.motherIncome,
  //     relationId: motherRelationGuid,
  //     nationalId: form.motherNationalId,
  //     workPlaceDetail: form.workPlaceDetail,
  //   });

  const toProfilePayload = (form: Record<string, any>): any => {
    // Build parents with NEW fields you added in UI
    const father = buildParent({
      relationship: "Father",
      firstName: form.fathersName,
      lastName: form.fatherLast,          // <-- NEW
      email: form.fatherEmail,              // <-- NEW
      phone: form.fatherMobile,             // keep your existing id if unchanged
      occupation: form.fatherOccupation,
      income: form.fatherIncome,
      gender: "Male",                       // <-- hard-code if you don’t collect it
      nationalId: form.fatherNationalId,
      workPlaceDetail: form.fatherWorkPlaceDetail,
    });

    const mother = buildParent({
      relationship: "Mother",
      firstName: form.motherName,
      lastName: form.motherLast,          // <-- NEW
      email: form.motherEmail,              // <-- NEW
      phone: form.motherMobile,             // <-- NEW (mother phone)
      occupation: form.motherOccupation,
      income: form.motherIncome,
      gender: "Female",                     // <-- hard-code if you don’t collect it
      nationalId: form.motherNationalId,
      workPlaceDetail: form.motherWorkPlaceDetail,
    });
    const relatives = [father, mother];

    // Bus route ID (if selected)
    const busRouteId = form.busRoute
      ? getRelationGuidByName(form.busRoute) // if your routes are fetched dynamically
      : null;

    return {
      // === Student Info ===
      firstName: form.firstName ?? "",
      middleName: form.middleName ?? "",
      lastName: form.lastName ?? "",
      dateOfBirth: toISO(form.dateOfBirth) ?? null,
      // gender: form.gender ?? "",
      gender: normalizeGender(form.gender),
      bloodGroup: form.bloodGroup ?? "",

      // === Contact ===
      email: form.email ?? "",
      phone: form.mobileNo ?? "",

      // === Address ===
      address1: form.address1 ?? "",
      address2: form.address2 ?? "",
      state: form.state ?? "",
      country: form.country ?? "",
      pincode: form.pincode ?? "",

      // === Emergency Contact ===
      emergencyContactName: form.Name ?? "",
      emergencyContactNumber: form.emergencyNumber ?? "",

      // === Education ===
      previousInstitution: form.previousInstitution ?? "",
      previousInstitutionAddress: form.address ?? "",
      languageOfInstruction: form.languageOfInstruction ?? "",
      specialEducationNeeds: form.specialEducationNeeds ?? "",

      // === Transport / Services ===
      transportRequired: !!form.busRoute || !!form.busfee,
      busRouteId: busRouteId ?? null,

      // === Other Info ===
      referralSource: form.referralSource ?? "",
      additionalInfo: form.additionalInfo ?? "",
      nativeLanguage: form.nativeLangauge ?? "",
      healthCondition: form.HealthIssueifany ?? "",
      comments: form.comment ?? "",
      consentToDataProcessing: !!form.consentToDataProcessing,

      // === Parent Relationships ===
      // relatives,
      ParentDetails: [father, mother],

      // === Misc ===
      isActive: true,
      modifiedBy: null,
    };
  };


  const handleFormSubmit = async (data: any) => {
    console.group("[ApplicationTrackerForm] handleFormSubmit()");
    console.log("Raw form data (from StudentForm):", data);
    try {
      if (!selectedStudent?.id) {
        alert("No student selected.");
        return;

      }

      // 1) Save the rest of the profile
      const payload = toProfilePayload(data);
      await apiClient.post(
        STUDENT_PROFILE_WITH_PARENTS(String(selectedStudent.id)),
        payload
      );

      // 2) Prepare uploads
      const uploads: Promise<any>[] = [];

      const photo: File | undefined =
        (Array.isArray(data.profilePicture) ? data.profilePicture[0] : data.profilePicture) as File | undefined;

      if (photo instanceof File) {
        // If your apiClient has the same "uploadBlob" helper:
        uploads.push(
          apiClient.uploadBlob(
            STUDENT_UPLOAD_PROFILE_PICTURE(String(selectedStudent.profileId)),
            photo,
            "File",                             // form-data field name
            { FileName: photo.name }            // optional meta if your API uses it
          )
        );


      }



      setFormSubmitted(true);
    } catch (e: any) {
      alert(
        e?.response?.data?.message ||
        "Failed to save profile or upload documents"
      );
    }
  };


  /** ---------- Current Status card for every state ---------- */
  function renderCurrentStatusCard() {
    const code = currentCode; // API code
    const bar = barStatus; // UI string

    const submittedOn = formatISTDateOnly(
      getStatusDateIso(selectedRegistration, "SUBMITTED")
    );
    const reviewedOn = formatISTDateOnly(
      getStatusDateIso(selectedRegistration, "REVIEWED")
    );
    const approvedOn = formatISTDateOnly(
      getStatusDateIso(selectedRegistration, "APPROVED")
    );
    const enrolledOn = formatISTDateOnly(
      getStatusDateIso(selectedRegistration, "ENROLLED")
    );

    if (!code || bar === "pending") return null;

    if (bar === "submitted") {
      return (
        <div className={styles.section}>
          <h3>Current Status</h3>
          <p>
            Your application has been submitted
            {submittedOn ? ` on ${submittedOn}` : ""}.
          </p>
          <div className={styles.visitBox}>
            <div>
              <p>What happens next?</p>
              <ul style={{ marginLeft: 16 }}>
                <li>Our team will review your application details.</li>
                <li>We’ll notify you by email when the review is complete.</li>
                <li>
                  If required, we’ll invite you to schedule a campus visit.
                </li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    if (bar === "reviewed") {
      return (
        <div className={styles.section}>
          <h3>Current Status</h3>
          <p>
            Your application is under review
            {reviewedOn ? ` as of ${reviewedOn}` : ""}.
          </p>
          <div className={styles.visitBox}>
            <div>
              <p>What to expect:</p>
              <ul style={{ marginLeft: 16 }}>
                <li>
                  We’ll reach out shortly to schedule a school visit (if
                  applicable).
                </li>
                <li>Keep an eye on your email for the invite.</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    if (bar === "visitScheduled") {
      return (
        <div className={styles.section}>
          <h3>Current Status</h3>
          <p>A school visit has been scheduled.</p>
          <div className={styles.visitBox}>
            <div>
              <ul style={{ marginLeft: 16, lineHeight: 1.8 }}>
                <li>
                  <strong>Date:</strong> {visitDate ?? "TBD"}
                </li>
                <li>
                  <strong>Start:</strong> {visitStartTime ?? "TBD"}
                </li>
                <li>
                  <strong>End:</strong> {visitEndTime ?? "TBD"}
                </li>
              </ul>

              <p>📍 {CAMPUS_VISIT_LOCATION} </p>
            </div>

            <div className={styles.buttons}>
              <a
                href={`${CAMPUS_VISIT_LOCATION_GOOGLE_URL}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button>Get Direction</Button>
              </a>
            </div>
          </div>
        </div>
      );
    }

    if (bar === "approved") {
      return (
        <div className={styles.section}>
          <h3>Current Status</h3>
          <p>
            Congratulations! Your application has been approved
            {approvedOn ? ` on ${approvedOn}` : ""}.
          </p>
          <div className={styles.visitBox}>
            <div>
              <p>Next steps:</p>
              <ul style={{ marginLeft: 16 }}>
                <li>
                  Complete the admission form below and upload the required
                  documents.
                </li>
                <li>
                  After submission, we’ll finalize enrollment and share
                  onboarding details.
                </li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    if (bar === "enrolled") {
      return (
        <div className={styles.section}>
          <h3>Current Status</h3>
          <p>
            🎉 The student is officially enrolled
            {enrolledOn ? ` as of ${enrolledOn}` : ""}.
          </p>
          <div className={styles.visitBox}>
            <div>
              <p>What’s next:</p>
              <ul style={{ marginLeft: 16 }}>
                <li>
                  Watch for a welcome email with orientation details and start
                  dates.
                </li>
                <li>
                  If anything looks incorrect, contact admissions support.
                </li>
              </ul>
            </div>
            <div className={styles.buttons}>
              <Button variant="primary" className={styles.secondaryBtn}>
                Download Welcome Pack
              </Button>
              <Button variant="primary" className={styles.secondaryBtn}>
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div>
      <div className="pt-5 mt-5 mb-5"></div>
      <div className={styles.wrapper}>
        {step === "email" && (
          <div className={styles.form}>
            <h2>Email Verification</h2>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <p className={styles.error}>{error}</p>}
            <button
              onClick={async () => {
                if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                  setError("Please enter a valid email");
                  return;
                }
                setError("");
                setLoading(true);
                try {
                  await apiClient.post(EMAIL_VERIFICATION(email), {});
                  setStep("otp");
                  fetchRegistrationsByEmail(email);
                  setTimeout(() => otpRefs.current[0]?.focus(), 0);
                } finally {
                  setLoading(false);
                }
              }}
              className={styles.primaryBtn}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </div>
        )}

        {step === "otp" && (
          <div className={styles.form}>
            <h2>Email Verification</h2>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <h2>Enter OTP</h2>
            <div className={styles.otpBox}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={otp[index]}
                  className={styles.otpInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    const next = [...otp];
                    next[index] = val;
                    setOtp(next);
                    if (val && index < otp.length - 1)
                      otpRefs.current[index + 1]?.focus();
                  }}
                  ref={(el) => {
                    otpRefs.current[index] = el;
                  }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  title={`OTP Digit ${index + 1}`}
                />
              ))}
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <button
              onClick={async () => {
                const code = otp.join("");
                if (code.length !== 6) {
                  setError("Please enter all 6 digits of OTP");
                  return;
                }
                setError("");
                setLoading(true);
                try {
                  await apiClient.post(
                    EMAIL_VERIFICATION_VERIFY(email, code),
                    {}
                  );
                  setStep("tracker");
                } finally {
                  setLoading(false);
                }
              }}
              className={styles.primaryBtn}
              disabled={loading || otp.join("").length !== 6}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        )}

        {step === "tracker" && (
          <>
            {!selectedStudent ? (
              <TrackApplicationCard
                students={students}
                onStudentClick={(id) => {
                  const s = students.find((x) => x.id === id) || null;
                  setSelectedStudent(s);
                }}
              />
            ) : (
              <>
                <Button
                  onClick={() => setSelectedStudent(null)}
                  variant="yellow"
                >
                  ← Back to Students
                </Button>

                <h2 className={styles.title}>Application Tracker</h2>
                <p className={styles.subtitle}>
                  Track the status of your child&apos;s admission in real-time.
                </p>

                <div className={styles.card}>
                  <div className={styles.prefviewformlink}>
                    <h3>{selectedStudent.name}</h3>
                  </div>
                  <div className={styles.cardcontainer}>
                    <div className={styles.profile}>
                      <div>
                        {selectedStudent.dateOfBirth && (
                          <p>Date of Birth: {selectedStudent.dateOfBirth}</p>
                        )}
                        <p>
                          Applying Course: {selectedStudent.courseDisplayName}
                        </p>
                      </div>
                    </div>
                    <div className={styles.meta}>
                      <p>Entry Year: {selectedStudent.entryYear}</p>
                      <p>Phone Number: {selectedStudent.phone}</p>
                    </div>
                  </div>
                </div>

                <ProgressBar currentStatus={barStatus} />
                {renderCurrentStatusCard()}

                {barStatus === "approved" && !formSubmitted && (
                  <div className={styles.section}>
                    <StudentForm
                      enquiryData={
                        selectedRegistration
                          ? toPrefill(selectedRegistration)
                          : {}
                      }
                      formConfig={admissionFormConfig}
                      onSubmit={handleFormSubmit}
                      // the three toggles controlling subsections inside Services
                      servicesEnabled={transportEnabled}
                      onToggleServices={setTransportEnabled}
                      onUploadDocuments={handleUploadDocuments}
                      canteenEnabled={canteenEnabled}
                      onToggleCanteen={setCanteenEnabled}
                      hostelEnabled={hostelEnabled}
                      onToggleHostel={setHostelEnabled}
                    />
                  </div>
                )}

                {formSubmitted && (
                  <SubmitSuccessModal
                    open={formSubmitted}
                    onClose={() => setFormSubmitted(false)}
                  />
                )}

                <ProgressHistory
                  studentStatuses={selectedRegistration?.studentStatuses}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ApplicationTrackerForm;
